import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Cookie, X, Settings, Check } from "lucide-react";

const COOKIE_CONSENT_KEY = "prenotafacile_cookie_consent";
const COOKIE_CONSENT_VERSION = "1.0";

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already given consent
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (consent) {
      try {
        const parsed = JSON.parse(consent);
        if (parsed.version === COOKIE_CONSENT_VERSION) {
          setPreferences(parsed.preferences);
          return; // Don't show banner if already consented
        }
      } catch (e) {
        // Invalid consent data, show banner
      }
    }
    // Show banner after a short delay for better UX
    const timer = setTimeout(() => setShowBanner(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      version: COOKIE_CONSENT_VERSION,
      preferences: prefs,
      timestamp: new Date().toISOString(),
    }));
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptAll = () => {
    const allAccepted = { necessary: true, analytics: true, marketing: true };
    setPreferences(allAccepted);
    saveConsent(allAccepted);
  };

  const acceptNecessary = () => {
    const necessaryOnly = { necessary: true, analytics: false, marketing: false };
    setPreferences(necessaryOnly);
    saveConsent(necessaryOnly);
  };

  const savePreferences = () => {
    saveConsent(preferences);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay for settings modal */}
      {showSettings && (
        <div 
          className="fixed inset-0 bg-black/50 z-[9998]" 
          onClick={() => setShowSettings(false)}
        />
      )}

      {/* Main Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 animate-in slide-in-from-bottom duration-300">
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden">
          {!showSettings ? (
            // Simple Banner View
            <div className="p-4 sm:p-6">
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex h-12 w-12 rounded-full bg-amber-100 items-center justify-center flex-shrink-0">
                  <Cookie className="h-6 w-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900 text-lg mb-2 flex items-center gap-2">
                    <Cookie className="h-5 w-5 text-amber-600 sm:hidden" />
                    Utilizziamo i Cookie
                  </h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Questo sito utilizza cookie tecnici necessari per il funzionamento e, 
                    con il tuo consenso, cookie di analisi e marketing per migliorare la tua esperienza.
                    Puoi accettare tutti i cookie o personalizzare le tue preferenze.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <Button
                      onClick={acceptAll}
                      className="bg-amber-500 hover:bg-amber-600 text-white"
                      data-testid="cookie-accept-all"
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Accetta tutti
                    </Button>
                    <Button
                      onClick={acceptNecessary}
                      variant="outline"
                      className="border-slate-300"
                      data-testid="cookie-accept-necessary"
                    >
                      Solo necessari
                    </Button>
                    <Button
                      onClick={() => setShowSettings(true)}
                      variant="ghost"
                      className="text-slate-600"
                      data-testid="cookie-settings"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Personalizza
                    </Button>
                  </div>
                </div>
                <button
                  onClick={acceptNecessary}
                  className="text-slate-400 hover:text-slate-600 p-1"
                  aria-label="Chiudi"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          ) : (
            // Settings View
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900 text-lg flex items-center gap-2">
                  <Settings className="h-5 w-5 text-slate-600" />
                  Preferenze Cookie
                </h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-slate-400 hover:text-slate-600 p-1"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-slate-900">Cookie Necessari</span>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Sempre attivi</span>
                  </div>
                  <p className="text-sm text-slate-600">
                    Essenziali per il funzionamento del sito. Includono autenticazione, 
                    preferenze di sessione e sicurezza.
                  </p>
                </div>

                {/* Analytics Cookies */}
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-slate-900">Cookie Analitici</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                    </label>
                  </div>
                  <p className="text-sm text-slate-600">
                    Ci aiutano a capire come i visitatori interagiscono con il sito, 
                    permettendoci di migliorare l&apos;esperienza utente.
                  </p>
                </div>

                {/* Marketing Cookies */}
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-slate-900">Cookie di Marketing</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                    </label>
                  </div>
                  <p className="text-sm text-slate-600">
                    Utilizzati per mostrare pubblicità pertinenti e misurare l&apos;efficacia 
                    delle campagne pubblicitarie.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <Button
                  onClick={savePreferences}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="cookie-save-preferences"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Salva preferenze
                </Button>
                <Button
                  onClick={acceptAll}
                  variant="outline"
                  data-testid="cookie-accept-all-settings"
                >
                  Accetta tutti
                </Button>
              </div>

              <p className="text-xs text-slate-500 mt-4">
                Per maggiori informazioni, consulta la nostra{" "}
                <a href="/privacy" className="text-amber-600 hover:underline">Privacy Policy</a>
                {" "}e la{" "}
                <a href="/cookie-policy" className="text-amber-600 hover:underline">Cookie Policy</a>.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CookieConsent;
